import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  SportsEsports as SportsEsportsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import FreeSampleSwitch from '../../../components/FreeSampleSwitch';

const GameTopicList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [subjectFilter, setSubjectFilter] = useState('');
  // "Free samples only": topics whose round plays on the public Games page.
  const [freeSampleOnly, setFreeSampleOnly] = useState(false);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    api.get('admin/game-topics/subjects').then((r) => {
      if (r.data?.success && r.data?.data) setSubjects(r.data.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchList();
  }, [page, rowsPerPage, subjectFilter, freeSampleOnly]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: rowsPerPage };
      if (subjectFilter) params.subject_id = subjectFilter;
      if (freeSampleOnly) params.free_sample = 1;
      const res = await api.get('admin/game-topics', { params });
      if (res.data?.success && res.data?.data) {
        const pag = res.data.data;
        setItems(Array.isArray(pag.data) ? pag.data : []);
        setTotalRecords(pag.total ?? 0);
      } else {
        setItems([]);
        setTotalRecords(0);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load game topics');
      setItems([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setSelectedItem(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    try {
      await api.delete(`admin/game-topics/${selectedItem.id}`);
      toast.success('Game topic deleted');
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 2 }}>
          Game Topics
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1 }}>
          <Typography variant="body1" sx={{ color: '#5a6c7d' }}>
            Manage which games appear under each subject and topic in the student portal.
          </Typography>
          {!loading && (
            <Chip label={`Total: ${totalRecords}`} size="small" sx={{ bgcolor: '#5e35b1', color: 'white' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Subject</InputLabel>
          <Select
            value={subjectFilter}
            onChange={(e) => { setSubjectFilter(e.target.value); setPage(0); }}
            label="Subject"
          >
            <MenuItem value="">All</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip
          label="Free samples only"
          color={freeSampleOnly ? 'success' : 'default'}
          variant={freeSampleOnly ? 'filled' : 'outlined'}
          onClick={() => { setFreeSampleOnly((v) => !v); setPage(0); }}
          sx={{ fontWeight: 600, mr: 'auto' }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/game-topics/add')}
          sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}
        >
          Add Game Topic
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Games</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Free sample</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell><Skeleton variant="text" width={200} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <SportsEsportsIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography color="text.secondary">No game topics found</Typography>
                    <Button startIcon={<AddIcon />} onClick={() => navigate('/admin/game-topics/add')} sx={{ mt: 2 }}>
                      Add Game Topic
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.subject_name || '—'}</TableCell>
                    <TableCell>{row.topic_name || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(row.games || []).slice(0, 5).map((g) => (
                          <Chip key={g} label={g} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                        ))}
                        {(row.games || []).length > 5 && (
                          <Chip label={`+${row.games.length - 5}`} size="small" sx={{ fontSize: '0.75rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FreeSampleSwitch
                        type="game_topic"
                        id={row.id}
                        value={row.is_free_sample}
                        onChange={(next) => freeSampleOnly && !next && fetchList()}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" title="View" onClick={() => navigate(`/admin/game-topics/${row.id}`)}>
                        <ViewIcon />
                      </IconButton>
                      <IconButton size="small" color="primary" onClick={() => navigate(`/admin/game-topics/${row.id}/edit`)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && items.length > 0 && (
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Game Topic</DialogTitle>
        <DialogContent>
          <Typography>
            Delete game topic for &quot;{selectedItem?.subject_name} → {selectedItem?.topic_name}&quot;? This will remove the games assigned to this topic in the student portal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GameTopicList;
